import React, { useEffect, useState } from "react";
// mui
import { Grid, TextField, Button, Select, MenuItem, FormControl, InputLabel, FormHelperText } from "@mui/material";
// mui icon
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
// react admin
import {
  useDataProvider,
  useNotify,
  useRedirect,
  TopToolbar,
} from "react-admin";
// loader
import { Loader } from "../../Loader";
// hook form
import { useForm, Controller } from "react-hook-form";
import { useLocation } from "react-router-dom";

const EditWheelSpin = () => {
  const location = useLocation();
  const record = location.state?.record;

  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();

  const [loading, setLoading] = useState(false);
  
  // Define reward types in frontend - easily configurable
  const rewardTypes = [
    { id: 1, name: "Crown" },
    { id: 2, name: "Gems" },
    { id: 3, name: "Good Luck" },
  ];

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm();

  useEffect(() => {
    if (record) {
      let slotsV2Data = [];
      let slotsData = [];
      
      // Handle slotsV2 field (V2 format)
      if (record.slotsV2 && Array.isArray(record.slotsV2) && record.slotsV2.length > 0) {
        slotsV2Data = record.slotsV2.map((slot) => ({
          amount: slot.Amount?.toString() || "",
          type: slot.Type || "",
        }));
      }
      
      // Handle SlotV1 (V1 format - simple numbers)
      if (record.slots && Array.isArray(record.slots) && record.slots.length > 0) {
        // Slots are already numbers, just convert to string for form fields
        slotsData = record.slots.map((amount) => amount?.toString() || "");
      }
      
      // Ensure we have at least 12 slots for slotsV2 and 16 for v1
      while (slotsV2Data.length < 12) {
        slotsV2Data.push({ amount: "", type: "" });
      }
      while (slotsData.length < 16) {
        slotsData.push("");
      }

      reset({
        day: record.day.toString(),
        minValue: record.minValue.toString(),
        maxValue: record.maxValue.toString(),
        countZero: record.countZero.toString(),
        spinsPerDay: record.spinsPerDay?.toString() || "1", // Default to 1 for V1 compatibility
        multiplier: record.multiplier?.toString() || "1", // Default to 1 for V1 compatibility
        slotsV2: slotsV2Data,
        slots: slotsData,
      });
    }
  }, [record, reset]);

  const onSubmit = async (formData) => {
    // Process slotsV2 data
    const filledSlotsV2 = formData.slotsV2?.filter(slot => 
      slot.amount && slot.amount.trim() !== "" && 
      slot.type && slot.type !== ""
    ).map((slot) => ({
      Amount: parseInt(slot.amount, 10),
      Type: parseInt(slot.type, 10),
    })) || [];

    // Process SlotsV1 (simple numbers)
    const filledSlots = formData.slots?.filter(slot => 
      slot && slot.trim() !== ""
    ).map((slot) => parseInt(slot, 10)) || [];

    const payload = {
      day: parseInt(formData.day, 10),
      minValue: parseInt(formData.minValue, 10),
      maxValue: parseInt(formData.maxValue, 10),
      countZero: parseInt(formData.countZero, 10),
      spinsPerDay: parseInt(formData.spinsPerDay, 10) || 1,
      multiplier: parseFloat(formData.multiplier) || 1,
    };

    // Include slotsV2 if there are filled slots
    if (filledSlotsV2.length > 0) {
      payload.slotsV2 = filledSlotsV2;
    }

    // Only add slots if there are filled slots
    if (filledSlots.length > 0) {
      payload.slots = filledSlots;
    }

    setLoading(true);
    try {
      const response = await dataProvider.update("SpinningWheel", {
        id: record?.id,
        data: payload,
      });
      if (response) {
        notify("Wheel Spin updated successfully!", { type: "success" });
        redirect("/SpinningWheel");
      }
    } catch (error) {
      notify(error.message, { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      {loading && <Loader />}
      <TopToolbar>
        <Button
          label="Back"
          variant="contained"
          color="primary"
          startIcon={<ArrowBackIcon />}
          sx={{
            mt: 3,
            background: "#FFC107",
            color: "#0A0A0A",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#FFA000",
            },
          }}
          onClick={() => redirect("/SpinningWheel")}
        >
          Back
        </Button>
      </TopToolbar>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} sx={{ mt: 3 }}>
          {/* Day Field */}
          <Grid item xs={3}>
            <Controller
              name="day"
              control={control}
              rules={{
                required: "Day is required",
                max: {
                  value: 7,
                  message: "Day must be less than 8",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Day"
                  fullWidth
                  error={!!errors.day}
                  helperText={errors.day?.message}
                  onKeyDown={(e) => {
                    const allowedKeys = [
                      "Backspace",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "Delete",
                    ];
                    if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{
                    autoComplete: "off",
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={3}>
            <Controller
              name="minValue"
              control={control}
              rules={{
                required: "Minimum Value is required",
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Minimum Value"
                  fullWidth
                  error={!!errors.minValue}
                  helperText={errors.minValue?.message}
                  onKeyDown={(e) => {
                    const allowedKeys = [
                      "Backspace",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "Delete",
                    ];

                    if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{
                    autoComplete: "off",
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={3}>
            <Controller
              name="maxValue"
              control={control}
              rules={{
                required: "Maximum Value is required",
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Maximum Value"
                  fullWidth
                  error={!!errors.maxValue}
                  helperText={errors.maxValue?.message}
                  onKeyDown={(e) => {
                    const allowedKeys = [
                      "Backspace",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "Delete",
                    ];

                    if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{
                    autoComplete: "off",
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={3}>
            <Controller
              name="countZero"
              control={control}
              rules={{
                required: "Better Luck is required",
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Better Luck"
                  fullWidth
                  error={!!errors.countZero}
                  helperText={errors.countZero?.message}
                  onKeyDown={(e) => {
                    const allowedKeys = [
                      "Backspace",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "Delete",
                    ];

                    if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{
                    autoComplete: "off",
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                />
              )}
            />
          </Grid>

          {/* Spins Per Day Field */}
          <Grid item xs={3}>
            <Controller
              name="spinsPerDay"
              control={control}
              rules={{
                required: "Spins per day is required",
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Spins Per Day"
                  fullWidth
                  error={!!errors.spinsPerDay}
                  helperText={errors.spinsPerDay?.message}
                  onKeyDown={(e) => {
                    const allowedKeys = [
                      "Backspace",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "Delete",
                    ];

                    if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  inputProps={{
                    autoComplete: "off",
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  }}
                />
              )}
            />
          </Grid>

          {/* Multiplier Field */}
          <Grid item xs={3}>
            <Controller
              name="multiplier"
              control={control}
              rules={{
                required: "Multiplier is required",
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Multiplier"
                  fullWidth
                  error={!!errors.multiplier}
                  helperText={errors.multiplier?.message}
                  inputProps={{
                    autoComplete: "off",
                  }}
                />
              )}
            />
          </Grid>

          {/* SlotsV2 Fields (V2 API Format) */}
          <Grid item xs={12}>
            <h3 style={{ color: "#FFC107", margin: "16px 0" }}>Slots (V2)</h3>
          </Grid>
          {Array.from({ length: 12 }).map((_, index) => (
            <Grid item xs={12} key={`slotsV2-${index}`}>
              <Grid container spacing={2}>
                {/* Amount Field */}
                <Grid item xs={6}>
                  <Controller
                    name={`slotsV2.${index}.amount`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={`SlotsV2 ${index + 1} - Amount`}
                        fullWidth
                        error={!!errors.slotsV2?.[index]?.amount}
                        helperText={errors.slotsV2?.[index]?.amount?.message}
                        onKeyDown={(e) => {
                          const allowedKeys = [
                            "Backspace",
                            "Tab",
                            "ArrowLeft",
                            "ArrowRight",
                            "Delete",
                          ];
                          if (
                            !/[0-9]/.test(e.key) &&
                            !allowedKeys.includes(e.key)
                          ) {
                            e.preventDefault();
                          }
                        }}
                        inputProps={{
                          autoComplete: "off",
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* Type Dropdown */}
                <Grid item xs={6}>
                  <Controller
                    name={`slotsV2.${index}.type`}
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.slotsV2?.[index]?.type}>
                        <InputLabel>{`SlotsV2 ${index + 1} - Reward Type`}</InputLabel>
                        <Select
                          {...field}
                          label={`SlotsV2 ${index + 1} - Reward Type`}
                          value={field.value || ""}
                          displayEmpty
                        >
                          {rewardTypes.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                              {type.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.slotsV2?.[index]?.type && (
                          <FormHelperText>{errors.slotsV2?.[index]?.type?.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>
              </Grid>
            </Grid>
          ))}

          {/* Legacy Slots Fields (V1 API Format - Simple Numbers) */}
          <Grid item xs={12}>
            <h3 style={{ color: "#FFC107", margin: "16px 0" }}>Slots (V1)</h3>
          </Grid>
          {Array.from({ length: 16 }).map((_, index) => (
            <Grid item xs={12} sm={3} key={`slots-${index}`}>
              <Controller
                name={`slots.${index}`}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={`Slot ${index + 1}`}
                    fullWidth
                    error={!!errors.slots?.[index]}
                    helperText={errors.slots?.[index]?.message}
                    onKeyDown={(e) => {
                      const allowedKeys = [
                        "Backspace",
                        "Tab",
                        "ArrowLeft",
                        "ArrowRight",
                        "Delete",
                      ];
                      if (
                        !/[0-9]/.test(e.key) &&
                        !allowedKeys.includes(e.key)
                      ) {
                        e.preventDefault();
                      }
                    }}
                    inputProps={{
                      autoComplete: "off",
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                    }}
                  />
                )}
              />
            </Grid>
          ))}

          <Grid item xs={12}>
            <Button type="submit" variant="contained" color="primary">
              Update
            </Button>
          </Grid>
        </Grid>
      </form>
    </React.Fragment>
  );
};

export default EditWheelSpin;
